import React, { useState, useEffect, useRef } from 'react';
import { API, fetchTasks as apiFetchTasks, fetchSites as apiFetchSites, fetchUsers as apiFetchUsers, getStatusColor, getPriorityColor } from '../utils/api';
import axios from 'axios';
import { 
  getWeekNumber, 
  getWeekDateRange, 
  getWeeksInYear, 
  getCurrentWeek, 
  isDateInWeekRange
} from '../utils/weekCalculations';
import AddTaskModal from './AddTaskModal';
import TaskGanttView from './TaskGanttView';
import ConfirmModal from './modals/ConfirmModal';
import SuccessModal from './modals/SuccessModal';
import ErrorModal from './modals/ErrorModal';

// SortIcon component moved outside to prevent re-creation on each render
const SortIcon = ({ field, sortField, sortDirection }) => {
  if (sortField !== field) {
    return (
      <svg className="w-4 h-4 text-gray-500 ml-1" fill="currentColor" viewBox="0 0 20 20">
        <path d="M5 12l5-5 5 5H5z"/>
      </svg>
    );
  }
  
  return sortDirection === 'asc' ? (
    <svg className="w-4 h-4 text-purple-400 ml-1" fill="currentColor" viewBox="0 0 20 20">
      <path d="M5 12l5-5 5 5H5z"/>
    </svg>
  ) : (
    <svg className="w-4 h-4 text-purple-400 ml-1" fill="currentColor" viewBox="0 0 20 20">
      <path d="M15 8l-5 5-5-5h10z"/>
    </svg>
  );
};

const TaskLists = () => {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [draggedTask, setDraggedTask] = useState(null);
  const [draggedUser, setDraggedUser] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [editTask, setEditTask] = useState({
    title: '', description: '', site_id: '', assigned_to: [], status: '', priority: '', start_date: '', due_date: '', task_capacity: 1.0
  });

  // Add Task Modal state
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);

  // File attachment states for edit/duplicate modal
  const [editSelectedFiles, setEditSelectedFiles] = useState([]);
  const [editUploadingFiles, setEditUploadingFiles] = useState(false);
  const editFileInputRef = useRef(null);

  // Site opening hours configuration for workload calculation
  const [siteOpeningHours, setSiteOpeningHours] = useState({
    working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    start_time: '08:00',
    end_time: '17:00',
    daily_break_duration: 1
  });

  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  const [successModal, setSuccessModal] = useState({ show: false, message: '' });
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });

  // Info section collapsed state — Page Tips removed (was unused).
  // Compact view toggle state
  const [isCompactView, setIsCompactView] = useState(false);

  // View mode: 'list' (weekly site columns) or 'gantt' (resource-axis Gantt).
  // Default = 'gantt' on first visit. localStorage respected only when the
  // user explicitly chose 'list' (so we never silently flip to list).
  const [viewMode, setViewMode] = useState(() => {
    try {
      const v = localStorage.getItem('tasksViewMode');
      return v === 'list' ? 'list' : 'gantt';
    } catch {
      return 'gantt';
    }
  });
  useEffect(() => {
    try { localStorage.setItem('tasksViewMode', viewMode); } catch { /* ignore */ }
  }, [viewMode]);

  // Signal that increments whenever the user (or the page) asks the Gantt to
  // scroll horizontally to the current week. Increments on Current Week click.
  // The Gantt also performs an initial scroll on its own mount. Task fetches
  // / drag-drop / edit do NOT bump this — scroll position is preserved.
  const [scrollToWeekSignal, setScrollToWeekSignal] = useState(0);

  // Selected year for filtering (simpler than week-based filtering)
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Helper function to get current week
  // Helper: "no filter" state — full year, every site, every status, every
  // priority, no search. Used by Clear All Filters and hasActiveFilters
  // comparison. Matches the initial useState defaults below.
  const getClearedFilters = () => {
    const { year } = getCurrentWeek();
    const totalWeeks = getWeeksInYear(year);
    return {
      fromYear: year,
      fromWeek: 1,
      toYear: year,
      toWeek: totalWeeks,
    };
  };

  // Filter states — initial state matches "cleared" state (full current year).
  const initialFilters = getClearedFilters();
  const [filters, setFilters] = useState({
    siteIds: [],
    fromYear: initialFilters.fromYear,
    fromWeek: initialFilters.fromWeek,
    toYear: initialFilters.toYear,
    toWeek: initialFilters.toWeek,
    statuses: [],
    priorities: [],
    searchText: ''
  });

  const goToCurrentYear = () => {
    const currentYear = new Date().getFullYear();
    setSelectedYear(currentYear);
    setFilters(prev => ({
      ...prev,
      fromYear: currentYear,
      fromWeek: 1,
      toYear: currentYear,
      toWeek: getWeeksInYear(currentYear)
    }));
  };

  // Year navigation functions
  const navigateYear = (direction) => {
    const newYear = selectedYear + direction;
    // Allow navigation from 2020 to current year + 1
    if (newYear >= 2020 && newYear <= new Date().getFullYear() + 1) {
      setSelectedYear(newYear);
      setFilters(prev => ({
        ...prev,
        fromYear: newYear,
        fromWeek: 1,
        toYear: newYear,
        toWeek: getWeeksInYear(newYear)
      }));
    }
  };

  // Scroll to current week. Affects both views:
  //  - List view: scrolls site columns into view via the existing data-week DOM hook.
  //  - Gantt view: bumps the scrollToWeekSignal which the Gantt subscribes to.
  const scrollToCurrentWeek = () => {
    const { week } = getCurrentWeek();
    // List view DOM scroll
    const weekElements = document.querySelectorAll(`[data-week="${week}"]`);
    weekElements.forEach(element => {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    // Gantt horizontal scroll — handled by TaskGanttView via prop
    setScrollToWeekSignal(s => s + 1);
  };

  useEffect(() => {
    fetchTasks();
    fetchSites();
    fetchUsers();
    fetchSiteOpeningHours();
  }, []);

  const fetchSiteOpeningHours = async () => {
    try {
      const response = await axios.get(`${API}/admin/organisation-settings`);
      if (response.data) {
        setSiteOpeningHours({
          working_days: response.data.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
          start_time: response.data.start_time || '08:00',
          end_time: response.data.end_time || '17:00',
          daily_break_duration: response.data.daily_break_duration || 1
        });
      }
    } catch (error) {
      console.error('Failed to fetch site opening hours:', error);
    }
  };

  // Calculate workload parameters based on site opening hours
  const getWorkloadParams = () => {
    const workingDaysCount = siteOpeningHours.working_days.length;
    
    // Calculate daily working hours (start to end minus break duration)
    const startTime = new Date(`2000-01-01T${siteOpeningHours.start_time}:00`);
    const endTime = new Date(`2000-01-01T${siteOpeningHours.end_time}:00`);
    const totalDailyHours = (endTime - startTime) / (1000 * 60 * 60); // Convert ms to hours
    const workingHoursPerDay = totalDailyHours - siteOpeningHours.daily_break_duration;
    
    // Maximum workload = number of working days per week
    const maxWorkload = workingDaysCount;
    
    // Step = 1 hour / working hours per day (so each day can be divided into hourly segments)
    const stepSize = 1 / workingHoursPerDay;
    
    return {
      maxWorkload,
      stepSize: Math.round(stepSize * 1000) / 1000, // Round to 3 decimal places
      workingHoursPerDay,
      workingDaysCount
    };
  };

  // Format workload display value
  const formatWorkloadDisplay = (value) => {
    const { workingHoursPerDay } = getWorkloadParams();
    const days = Math.floor(value);
    const remainingHours = (value - days) * workingHoursPerDay;
    
    if (days === 0) {
      return `${remainingHours.toFixed(1)}h`;
    } else if (remainingHours === 0) {
      return `${days} day${days !== 1 ? 's' : ''}`;
    } else {
      return `${days} day${days !== 1 ? 's' : ''} ${remainingHours.toFixed(1)}h`;
    }
  };

  const fetchTasks = async () => {
    try {
      setIsLoading(true);
      const data = await apiFetchTasks();
      setTasks(data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Drag-to-reschedule handler — wired from the Gantt's bar/handle drags.
  // Optimistically patches the task in local state, fires PUT, falls back
  // to refetch on success or revert on error.
  const handleGanttReschedule = async (taskId, payload) => {
    const original = tasks.find(t => t.id === taskId);
    if (!original) return;
    // Optimistic update
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, ...payload } : t));
    try {
      await axios.put(`${API}/admin/tasks/${taskId}`, payload);
      // Refetch silently to capture any server-side normalisation.
      fetchTasks();
    } catch (error) {
      console.error('Failed to reschedule task via Gantt drag:', error);
      // Revert optimistic update
      setTasks(prev => prev.map(t => t.id === taskId ? original : t));
      setErrorModal({
        show: true,
        message: 'Failed to reschedule task: ' + (error.response?.data?.detail || 'Unknown error')
      });
    }
  };

  const fetchSites = async () => {
    try {
      const data = await apiFetchSites();
      setSites(data);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await apiFetchUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  // Filter tasks based on current filter criteria
  const getFilteredTasks = () => {
    return tasks.filter(task => {
      // Site filter (multi-select)
      if (filters.siteIds.length > 0 && !filters.siteIds.includes(task.site_id)) {
        return false;
      }

      // Week range filter — anchored to start_date when present (falls back to due_date for older tasks). Tasks with neither pass through as unscheduled.
      if (filters.fromYear && filters.fromWeek && filters.toYear && filters.toWeek) {
        const anchor = task.start_date || task.due_date;
        if (anchor && !isDateInWeekRange(anchor, filters.fromYear, filters.fromWeek, filters.toYear, filters.toWeek)) {
          return false;
        }
      }

      // Status filter (multi-select)
      if (filters.statuses.length > 0 && !filters.statuses.includes(task.status)) {
        return false;
      }

      // Priority filter (multi-select)
      if (filters.priorities.length > 0 && !filters.priorities.includes(task.priority)) {
        return false;
      }

      // Free text search
      if (filters.searchText) {
        const searchLower = filters.searchText.toLowerCase();
        const matchesTitle = task.title.toLowerCase().includes(searchLower);
        const matchesDescription = task.description && task.description.toLowerCase().includes(searchLower);
        const matchesSite = task.site_name && task.site_name.toLowerCase().includes(searchLower);
        const matchesAssignees = task.assigned_user_names && 
          task.assigned_user_names.some(name => name.toLowerCase().includes(searchLower));
        
        if (!matchesTitle && !matchesDescription && !matchesSite && !matchesAssignees) {
          return false;
        }
      }

      return true;
    });
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleMultiSelectChange = (filterType, value, checked) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: checked 
        ? [...prev[filterType], value]
        : prev[filterType].filter(item => item !== value)
    }));
  };

  const clearFilters = () => {
    const cleared = getClearedFilters();
    setSelectedYear(cleared.fromYear); // keep chart axis in sync with filter year
    setFilters({
      siteIds: [],
      fromYear: cleared.fromYear,
      fromWeek: cleared.fromWeek,
      toYear: cleared.toYear,
      toWeek: cleared.toWeek,
      statuses: [],
      priorities: [],
      searchText: ''
    });
  };

  const hasActiveFilters = () => {
    const cleared = getClearedFilters();
    return filters.siteIds.length > 0 ||
           filters.statuses.length > 0 ||
           filters.priorities.length > 0 ||
           filters.searchText ||
           (filters.fromYear !== cleared.fromYear || filters.fromWeek !== cleared.fromWeek) ||
           (filters.toYear !== cleared.toYear || filters.toWeek !== cleared.toWeek);
  };

  // Available statuses and priorities for filters
  const availableStatuses = ['pending', 'in_progress', 'completed', 'cancelled'];
  const availablePriorities = ['low', 'medium', 'high', 'urgent'];

  // Weekly calendar utilities - Using standardized week calculation utilities
  const getWeeklyMarkers = () => {
    const weeks = [];
    const totalWeeks = getWeeksInYear(selectedYear);
    
    // Only show weeks within the selected filter range
    const startWeek = Math.max(1, filters.fromWeek);
    const endWeek = Math.min(totalWeeks, filters.toWeek);
    
    for (let week = startWeek; week <= endWeek; week++) {
      const { start, startStr, endStr } = getWeekDateRange(selectedYear, week);
      
      weeks.push({
        weekNumber: week,
        mondayDate: start,
        dateRange: `${startStr} - ${endStr}`
      });
    }
    
    return weeks;
  };

  const getTaskWeek = (task) => {
    // Prefer start_date for week placement so tasks land on the week they
    // BEGIN. Fall back to due_date for older tasks that don't have a start
    // date yet — keeps existing behaviour intact for unmigrated data.
    const anchorDateStr = task.start_date || task.due_date;
    if (!anchorDateStr) return null;
    
    const dueDate = new Date(anchorDateStr);
    const taskYear = dueDate.getFullYear();
    
    // Only return week number if the task is in the selected year
    if (taskYear !== selectedYear) {
      // Check if this date falls into week 1 of selected year (which may include days from previous year)
      // or last week of selected year (which may include days from next year)
      const weekNum = getWeekNumber(dueDate, selectedYear);
      const { start, end } = getWeekDateRange(selectedYear, weekNum);
      
      if (dueDate >= start && dueDate <= end) {
        return weekNum;
      }
      return null;
    }
    
    const weekNumber = getWeekNumber(dueDate, selectedYear);
    const totalWeeks = getWeeksInYear(selectedYear);
    
    return weekNumber >= 1 && weekNumber <= totalWeeks ? weekNumber : null;
  };

  const getMondayOfWeek = (weekNumber) => {
    const { start } = getWeekDateRange(selectedYear, weekNumber);
    return start;
  };

  const getTasksBySite = () => {
    const tasksBySite = {};
    const filteredTasksData = getFilteredTasks(); // Use filtered tasks instead of all tasks
    
    // First, initialize all sites from the sites array with empty structure
    sites.forEach(site => {
      tasksBySite[site.site_name] = {
        unscheduled: []
      };
    });
    
    // Then populate with filtered tasks (Unassigned category created only if needed)
    filteredTasksData.forEach(task => {
      const siteName = task.site_name || 'Unassigned';
      
      // Ensure the site exists in tasksBySite (in case task has a site_name not in sites array or is unassigned)
      if (!tasksBySite[siteName]) {
        tasksBySite[siteName] = {
          unscheduled: []
        };
      }
      
      const weekNumber = getTaskWeek(task);
      if (weekNumber) {
        // Only include weeks that fall within the selected range
        if (weekNumber >= filters.fromWeek && weekNumber <= filters.toWeek) {
          if (!tasksBySite[siteName][weekNumber]) {
            tasksBySite[siteName][weekNumber] = [];
          }
          tasksBySite[siteName][weekNumber].push(task);
        }
      } else {
        tasksBySite[siteName]['unscheduled'].push(task);
      }
    });
    
    return tasksBySite;
  };

  // Get users assigned to each site
  const getUsersBySite = () => {
    const usersBySite = {};
    
    sites.forEach(site => {
      const siteUsers = users.filter(user => 
        user.assigned_sites && user.assigned_sites.includes(site.id)
      );
      usersBySite[site.site_name] = siteUsers;
    });
    
    return usersBySite;
  };

  // Drag and drop handlers
  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    setDraggedUser(null); // Clear any user drag state
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleUserDragStart = (e, user) => {
    setDraggedUser(user);
    setDraggedTask(null); // Clear any task drag state
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = draggedTask ? 'move' : 'copy';
  };

  const handleDrop = async (e, weekNumber, siteName) => {
    e.preventDefault();
    
    if (draggedTask) {
      // Handle task movement between weeks
      try {
        const mondayDate = getMondayOfWeek(weekNumber);
        
        console.log(`Moving task "${draggedTask.title}" to Week ${weekNumber}`);
        console.log(`Calculated Monday date: ${mondayDate.toDateString()} (day of week: ${mondayDate.getDay()})`);
        
        const mondayStartOfDay = new Date(mondayDate.getFullYear(), mondayDate.getMonth(), mondayDate.getDate(), 12, 0, 0);
        
        const updateData = {
          due_date: mondayStartOfDay.toISOString()
        };
        
        console.log(`Sending due_date: ${updateData.due_date}`);
        
        await axios.put(`${API}/admin/tasks/${draggedTask.id}`, updateData);
        
        fetchTasks();
        
        const dayName = mondayStartOfDay.toLocaleDateString('en-US', { weekday: 'long' });
        setSuccessModal({
          show: true,
          message: `Task "${draggedTask.title}" moved to Week ${weekNumber}\nDue date set to: ${dayName}, ${mondayStartOfDay.toLocaleDateString()}`
        });
        
      } catch (error) {
        console.error('Failed to update task:', error);
        setErrorModal({
          show: true,
          message: 'Failed to update task: ' + (error.response?.data?.detail || 'Unknown error')
        });
      }
    }
    
    setDraggedTask(null);
    setDraggedUser(null);
  };

  const handleTaskDrop = async (e, task) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (draggedUser) {
      // Handle user assignment to task
      try {
        // Add user to task if not already assigned
        const currentAssignees = task.assigned_to || [];
        if (!currentAssignees.includes(draggedUser.id)) {
          const updatedAssignees = [...currentAssignees, draggedUser.id];
          
          const updateData = {
            assigned_to: updatedAssignees
          };
          
          await axios.put(`${API}/admin/tasks/${task.id}`, updateData);
          
          fetchTasks();
          
          setSuccessModal({
            show: true,
            message: `User "${draggedUser.full_name}" assigned to task "${task.title}"`
          });
        } else {
          setErrorModal({
            show: true,
            message: `User "${draggedUser.full_name}" is already assigned to this task`
          });
        }
        
      } catch (error) {
        console.error('Failed to assign user to task:', error);
        setErrorModal({
          show: true,
          message: 'Failed to assign user: ' + (error.response?.data?.detail || 'Unknown error')
        });
      }
    }
    
    setDraggedUser(null);
  };

  const handleRemoveUserFromTask = async (task, userId) => {
    try {
      const currentAssignees = task.assigned_to || [];
      const updatedAssignees = currentAssignees.filter(id => id !== userId);
      
      const updateData = {
        assigned_to: updatedAssignees
      };
      
      await axios.put(`${API}/admin/tasks/${task.id}`, updateData);
      
      fetchTasks();
      
      // Find user name for the message
      const user = users.find(u => u.id === userId);
      const userName = user ? user.full_name : 'Unknown User';
      
      setSuccessModal({
        show: true,
        message: `User "${userName}" removed from task "${task.title}"`
      });
      
    } catch (error) {
      console.error('Failed to remove user from task:', error);
      setErrorModal({
        show: true,
        message: 'Failed to remove user: ' + (error.response?.data?.detail || 'Unknown error')
      });
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setEditTask({
      title: task.title,
      description: task.description || '',
      site_id: task.site_id || '',
      assigned_to: task.assigned_to || [],
      status: task.status,
      priority: task.priority || 'medium',
      start_date: task.start_date ? task.start_date.split('T')[0] : '',
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      task_capacity: task.task_capacity || 1.0
    });
    
    // Clear file selection
    setEditSelectedFiles([]);
  };

  const handleDuplicateTask = (task) => {
    // Set editing task to indicate this is a duplicate (new task)
    setEditingTask({ ...task, isDuplicate: true });
    setEditTask({
      title: `${task.title} (Copy)`,
      description: task.description || '',
      site_id: task.site_id || '',
      assigned_to: task.assigned_to || [],
      status: 'pending', // Reset status to pending for duplicates
      priority: task.priority || 'medium',
      start_date: '', // Clear start date for duplicates
      due_date: '', // Clear due date for duplicates
      task_capacity: task.task_capacity || 1.0
    });
    
    // Clear file selection for duplicates
    setEditSelectedFiles([]);
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    try {
      let taskId = null;
      let uploadFailed = false;
      let uploadErrorMessage = '';
      
      // Check if this is a duplicate operation
      if (editingTask.isDuplicate) {
        // Create a new task for duplicates
        const taskData = {
          title: editTask.title,
          description: editTask.description,
          site_id: editTask.site_id,
          assigned_to: editTask.assigned_to,
          status: editTask.status,
          priority: editTask.priority,
          start_date: editTask.start_date ? new Date(editTask.start_date).toISOString() : null,
          due_date: editTask.due_date ? new Date(editTask.due_date).toISOString() : null,
          task_capacity: editTask.task_capacity
        };
        
        const response = await axios.post(`${API}/admin/tasks`, taskData);
        taskId = response.data.id;
        
        // Upload attachments if any
        if (editSelectedFiles.length > 0) {
          setEditUploadingFiles(true);
          try {
            await uploadEditAttachments(taskId);
          } catch (uploadError) {
            uploadFailed = true;
            uploadErrorMessage = uploadError.message || 'Some file uploads failed.';
          } finally {
            setEditUploadingFiles(false);
          }
        }
        
        setEditingTask(null);
        setEditTask({ title: '', description: '', site_id: '', assigned_to: [], status: '', priority: '', due_date: '', task_capacity: 1.0 });
        fetchTasks();
        setEditSelectedFiles([]);
        
        if (uploadFailed) {
          setErrorModal({
            show: true,
            message: `Task duplicated, but attachment upload failed: ${uploadErrorMessage}`
          });
        } else {
          setSuccessModal({
            show: true,
            message: 'Task duplicated successfully!'
          });
        }
      } else {
        // Update existing task
        taskId = editingTask.id;
        const updateData = {};
        if (editTask.title !== editingTask.title) updateData.title = editTask.title;
        if (editTask.description !== editingTask.description) updateData.description = editTask.description;
        if (editTask.site_id !== editingTask.site_id) updateData.site_id = editTask.site_id;
        if (JSON.stringify(editTask.assigned_to) !== JSON.stringify(editingTask.assigned_to || [])) updateData.assigned_to = editTask.assigned_to;
        if (editTask.status !== editingTask.status) updateData.status = editTask.status;
        if (editTask.priority !== editingTask.priority) updateData.priority = editTask.priority;
        if (editTask.due_date !== (editingTask.due_date ? editingTask.due_date.split('T')[0] : '')) {
          updateData.due_date = editTask.due_date ? new Date(editTask.due_date).toISOString() : null;
        }
        if (editTask.start_date !== (editingTask.start_date ? editingTask.start_date.split('T')[0] : '')) {
          updateData.start_date = editTask.start_date ? new Date(editTask.start_date).toISOString() : null;
        }
        if (editTask.task_capacity !== (editingTask.task_capacity || 1.0)) updateData.task_capacity = editTask.task_capacity;

        await axios.put(`${API}/admin/tasks/${editingTask.id}`, updateData);
        
        // Upload attachments if any
        if (editSelectedFiles.length > 0) {
          setEditUploadingFiles(true);
          try {
            await uploadEditAttachments(taskId);
          } catch (uploadError) {
            uploadFailed = true;
            uploadErrorMessage = uploadError.message || 'Some file uploads failed.';
          } finally {
            setEditUploadingFiles(false);
          }
        }
        
        fetchTasks();
        setEditingTask(null);
        setEditTask({ title: '', description: '', site_id: '', assigned_to: [], status: '', priority: '', due_date: '', task_capacity: 1.0 });
        setEditSelectedFiles([]);
        
        if (uploadFailed) {
          setErrorModal({
            show: true,
            message: `Task updated, but attachment upload failed: ${uploadErrorMessage}`
          });
        } else {
          setSuccessModal({
            show: true,
            message: 'Task updated successfully!'
          });
        }
      }
    } catch (error) {
      const operation = editingTask.isDuplicate ? 'duplicate' : 'update';
      setErrorModal({
        show: true,
        message: `Failed to ${operation} task: ` + (error.response?.data?.detail || 'Unknown error')
      });
    }
  };

  const cancelEditTask = () => {
    setEditingTask(null);
    setEditTask({ title: '', description: '', site_id: '', assigned_to: [], status: '', priority: '', due_date: '', task_capacity: 1.0 });
    setEditSelectedFiles([]);
  };

  // File attachment handling for edit/duplicate modal
  const handleEditFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxFiles = 5;
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    // Filter valid files
    const validFiles = files.filter(file => {
      if (file.size > maxSize) {
        setErrorModal({
          show: true,
          message: `File "${file.name}" exceeds 10MB limit`
        });
        return false;
      }
      return true;
    });
    
    // Check total count
    const totalFiles = editSelectedFiles.length + validFiles.length;
    if (totalFiles > maxFiles) {
      setErrorModal({
        show: true,
        message: `Maximum ${maxFiles} attachments allowed`
      });
      return;
    }
    
    setEditSelectedFiles(prev => [...prev, ...validFiles]);
    
    // Reset input
    if (editFileInputRef.current) {
      editFileInputRef.current.value = '';
    }
  };

  const removeEditFile = (index) => {
    setEditSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadEditAttachments = async (taskId) => {
    const token = localStorage.getItem('token');
    const errors = [];
    
    for (const file of editSelectedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        await axios.post(
          `${API}/admin/tasks/${taskId}/attachments`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } catch (error) {
        const errorMessage = error.response?.data?.detail || `Failed to upload ${file.name}`;
        errors.push(errorMessage);
      }
    }
    
    if (errors.length > 0) {
      const error = new Error(errors[0]);
      error.allErrors = errors;
      throw error;
    }
  };

  const handleDeleteTask = (task) => {
    setConfirmModal({
      show: true,
      message: `Are you sure you want to delete the task "${task.title}"?`,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/admin/tasks/${task.id}`);
          fetchTasks();
          setSuccessModal({
            show: true,
            message: 'Task deleted successfully!'
          });
        } catch (error) {
          setErrorModal({
            show: true,
            message: 'Failed to delete task: ' + (error.response?.data?.detail || 'Unknown error')
          });
        }
      }
    });
  };

  const tasksBySite = getTasksBySite();
  const usersBySite = getUsersBySite();

  // TaskCard Component for reusable task display (compact version for column layout)
  // Helper function to format user name as F.Lastname
  const formatUserName = (fullName) => {
    if (!fullName) return '';
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return fullName;
    const firstName = parts[0];
    const lastName = parts[parts.length - 1];
    return `${firstName.charAt(0)}.${lastName}`;
  };

  // Helper function to format task duration
  const formatDuration = (days) => {
    const numDays = parseFloat(days) || 1;
    if (numDays === 1) return '1 day';
    if (numDays < 1) return `${(numDays * 8).toFixed(1)} hours`;
    return `${numDays} days`;
  };

  // Get priority badge colors (dark theme)
  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500 text-white';
      case 'high': return 'bg-red-500 text-white';
      case 'medium': return 'bg-amber-500 text-white';
      case 'low': return 'bg-slate-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  // Get status badge colors (dark theme)
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500 text-white';
      case 'in_progress': return 'bg-blue-500 text-white';
      case 'pending': return 'bg-yellow-500 text-white';
      case 'cancelled': return 'bg-gray-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const TaskCard = ({ task, isCompact }) => {
    const hasMaterials = task.linked_materials && task.linked_materials.length > 0;
    const hasAttachments = task.attachments && task.attachments.length > 0;
    
    // Compact View - Single line with task name and due date only
    if (isCompact) {
      return (
        <div className="flex items-center justify-between gap-2 py-1">
          <h4 className={`font-medium text-sm truncate flex-1 ${task.status === 'completed' ? 'text-gray-500 line-through' : 'text-white'}`}>
            {task.title}
          </h4>
          {task.due_date && (
            <span className={`text-xs flex-shrink-0 ${task.status === 'completed' ? 'text-gray-500' : 'text-gray-400'}`}>
              {(() => {
                const date = new Date(task.due_date);
                return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
              })()}
            </span>
          )}
        </div>
      );
    }
    
    return (
      <div className="flex flex-col h-full">
        {/* Task Header - Title and Actions */}
        <div className="flex items-start justify-between mb-2">
          <h4 className={`font-bold text-sm line-clamp-2 flex-1 ${task.status === 'completed' ? 'text-gray-500 line-through' : 'text-white'}`}>
            {task.title}
          </h4>
          <div className="flex items-center space-x-0.5 ml-2 flex-shrink-0">
            <button
              onClick={() => handleEditTask(task)}
              className="p-1.5 text-gray-400 hover:text-purple-400 hover:bg-slate-700 rounded-md transition-all duration-200"
              title="Edit Task"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={() => handleDuplicateTask(task)}
              className="p-1.5 text-gray-400 hover:text-emerald-400 hover:bg-slate-700 rounded-md transition-all duration-200"
              title="Duplicate Task"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
            <button
              onClick={() => handleDeleteTask(task)}
              className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-slate-700 rounded-md transition-all duration-200"
              title="Delete Task"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Date row — shows "start → due" when both present, otherwise whichever is set */}
        {(task.start_date || task.due_date) && (
          <div className={`flex items-center text-xs mb-2 ${task.status === 'completed' ? 'text-gray-500' : 'text-gray-300'}`}>
            <svg className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {(() => {
              const fmt = (iso) => {
                const date = new Date(iso);
                const dayOfWeek = date.toLocaleDateString('en-GB', { weekday: 'short' });
                const dayMonth = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
                return `${dayMonth} (${dayOfWeek})`;
              };
              if (task.start_date && task.due_date) {
                return `${fmt(task.start_date)} → ${fmt(task.due_date)}`;
              }
              return fmt(task.start_date || task.due_date);
            })()}
          </div>
        )}

        {/* Assigned Users with User Icon and Pills */}
        {task.assigned_user_names && task.assigned_user_names.length > 0 && (
          <div className="flex items-start mb-2">
            <svg className={`w-3.5 h-3.5 mr-1.5 mt-0.5 flex-shrink-0 ${task.status === 'completed' ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <div className="flex flex-wrap gap-1 flex-1">
              {task.assigned_user_names.map((userName, index) => {
                const user = users.find(u => u.full_name === userName);
                const userId = user ? user.id : null;
                
                return (
                  <div 
                    key={index}
                    className={`inline-flex items-center rounded-full border ${
                      task.status === 'completed' ? 'bg-slate-600 border-slate-500 text-gray-400' : 'bg-slate-600 border-slate-500 text-gray-200'
                    }`}
                  >
                    <span className="pl-2 pr-1 py-0.5 text-xs font-medium">
                      {formatUserName(userName)}
                    </span>
                    {userId && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveUserFromTask(task, userId);
                        }}
                        className={`mr-1 transition-colors duration-200 ${
                          task.status === 'completed' 
                            ? 'text-gray-500 hover:text-red-400' 
                            : 'text-gray-400 hover:text-red-400'
                        }`}
                        title={`Remove ${userName}`}
                      >
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                        </svg>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Spacer to push bottom row down */}
        <div className="flex-grow"></div>

        {/* Bottom Row - Status, Priority, Icons, Duration */}
        <div className="flex items-center justify-between pt-2 border-t border-slate-700">
          {/* Left: Status Badge + Priority Badge + Material & Attachment Icons */}
          <div className="flex items-center gap-1.5">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(task.status)}`}>
              {task.status.replace('_', ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
            </span>
            
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium uppercase ${getPriorityBadgeColor(task.priority)}`}>
              {task.priority}
            </span>
            
            {/* Materials Icon */}
            <div className={`${hasMaterials ? 'text-purple-400' : 'text-gray-600'}`} title={hasMaterials ? `${task.linked_materials.length} material(s)` : 'No materials'}>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            
            {/* Attachments Icon */}
            <div className={`${hasAttachments ? 'text-purple-400' : 'text-gray-600'}`} title={hasAttachments ? `${task.attachments.length} attachment(s)` : 'No attachments'}>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            </div>
          </div>

          {/* Right: Duration */}
          <span className={`text-xs ${task.status === 'completed' ? 'text-gray-500' : 'text-gray-400'}`}>
            {formatDuration(task.task_capacity)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="min-h-screen bg-slate-900 -m-8 p-8">
        {/* Modern Header - Purple Gradient */}
        <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-1">Tasks</h1>
              <p className="text-purple-200">Create & Assign Tasks</p>
            </div>
            <button
              onClick={() => setShowAddTaskModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-400 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>New Task</span>
            </button>
          </div>
        </div>
      
        <div className="bg-slate-800 rounded-xl shadow-lg border border-slate-700 p-6">
      
        {/* Filter Panel - Dark Theme */}
        <div className="mb-4 bg-slate-900 rounded-xl p-4 border border-slate-700 sticky top-0 z-40">
          {/* Top Row: Status, Priority, Search filters + Year Selector + Buttons */}
          <div className="flex items-start justify-between gap-4 mb-3">
            {/* Left side: Status, Priority, Search */}
            <div className="flex items-center gap-4 flex-wrap flex-1">
              {/* Status Filter */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-gray-400 uppercase">Status:</span>
                {availableStatuses.map((status) => {
                  const isSelected = filters.statuses.includes(status);
                  return (
                    <button
                      key={status}
                      onClick={() => handleMultiSelectChange('statuses', status, !isSelected)}
                      className={`px-2 py-0.5 rounded text-xs font-medium transition-all duration-200 border ${
                        isSelected
                          ? 'bg-purple-600 text-white border-purple-500'
                          : 'bg-slate-800 text-gray-300 border-slate-600 hover:border-purple-500 hover:text-white'
                      }`}
                    >
                      {status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1)}
                    </button>
                  );
                })}
              </div>

              {/* Priority Filter */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs font-medium text-gray-400 uppercase">Priority:</span>
                {availablePriorities.map((priority) => {
                  const isSelected = filters.priorities.includes(priority);
                  return (
                    <button
                      key={priority}
                      onClick={() => handleMultiSelectChange('priorities', priority, !isSelected)}
                      className={`px-2 py-0.5 rounded text-xs font-medium transition-all duration-200 border ${
                        isSelected
                          ? 'bg-purple-600 text-white border-purple-500'
                          : 'bg-slate-800 text-gray-300 border-slate-600 hover:border-purple-500 hover:text-white'
                      }`}
                    >
                      {priority.charAt(0).toUpperCase() + priority.slice(1)}
                    </button>
                  );
                })}
              </div>

              {/* Compact View Toggle */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400">Compact View</span>
                <button
                  onClick={() => setIsCompactView(!isCompactView)}
                  className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${
                    isCompactView ? 'bg-purple-600' : 'bg-slate-700'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform duration-200 ${
                      isCompactView ? 'translate-x-4' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* View Mode Toggle (List ⇄ Gantt) */}
              <div className="flex items-center gap-1 bg-slate-800 border border-slate-600 rounded-md p-0.5" data-testid="view-mode-toggle">
                <button
                  type="button"
                  onClick={() => setViewMode('list')}
                  className={`px-2.5 py-0.5 rounded text-xs font-medium transition-colors ${
                    viewMode === 'list' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:text-white'
                  }`}
                  data-testid="view-mode-list-btn"
                  title="Weekly site columns view"
                >
                  List
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('gantt')}
                  className={`px-2.5 py-0.5 rounded text-xs font-medium transition-colors ${
                    viewMode === 'gantt' ? 'bg-purple-600 text-white' : 'text-gray-300 hover:text-white'
                  }`}
                  data-testid="view-mode-gantt-btn"
                  title="Team-member timeline (Gantt)"
                >
                  Gantt
                </button>
              </div>
            </div>

            {/* Right side: Year Selector + Current Year + Current Week buttons */}
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              <div className="flex items-center gap-2">
                {/* Year Selector - 20% smaller */}
                <div className="flex items-center bg-slate-800 rounded border border-slate-600">
                  <button
                    onClick={() => navigateYear(-1)}
                    disabled={selectedYear <= 2020}
                    className="p-1.5 hover:bg-slate-700 rounded-l transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Previous Year"
                  >
                    <svg className="w-3.5 h-3.5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <div className="px-3 py-1 font-semibold text-white min-w-[60px] text-center text-sm">
                    {selectedYear}
                  </div>
                  <button
                    onClick={() => navigateYear(1)}
                    disabled={selectedYear >= new Date().getFullYear() + 1}
                    className="p-1.5 hover:bg-slate-700 rounded-r transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Next Year"
                  >
                    <svg className="w-3.5 h-3.5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                {/* Current Year Button */}
                <button
                  onClick={goToCurrentYear}
                  disabled={selectedYear === new Date().getFullYear()}
                  className={`px-2.5 py-1.5 rounded text-xs font-medium transition-all duration-200 ${
                    selectedYear === new Date().getFullYear()
                      ? 'bg-slate-700 text-gray-500 cursor-not-allowed'
                      : 'bg-purple-600 hover:bg-purple-500 text-white shadow-md hover:shadow-lg'
                  }`}
                >
                  Current Year
                </button>

                {/* Current Week Button */}
                <button
                  onClick={scrollToCurrentWeek}
                  disabled={selectedYear !== new Date().getFullYear()}
                  className={`px-2.5 py-1.5 rounded text-xs font-medium transition-all duration-200 ${
                    selectedYear !== new Date().getFullYear()
                      ? 'bg-slate-700 text-gray-500 cursor-not-allowed'
                      : 'bg-purple-600 hover:bg-purple-500 text-white shadow-md hover:shadow-lg'
                  }`}
                >
                  Current Week
                </button>

                {/* New Task Button (sticky filter row) */}
                <button
                  onClick={() => setShowAddTaskModal(true)}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium bg-emerald-600 hover:bg-emerald-500 text-white shadow-md hover:shadow-lg transition-all duration-200"
                  data-testid="sticky-new-task-btn"
                  title="Create a new task"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>New Task</span>
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Row: Site Filter + Search */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-gray-400 uppercase">Site:</span>
              {sites.map((site) => {
                const isSelected = filters.siteIds.includes(site.id);
                return (
                  <button
                    key={site.id}
                    onClick={() => handleMultiSelectChange('siteIds', site.id, !isSelected)}
                    className={`px-2 py-0.5 rounded text-xs font-medium transition-all duration-200 border ${
                      isSelected
                        ? 'bg-purple-600 text-white border-purple-500'
                        : 'bg-slate-800 text-gray-300 border-slate-600 hover:border-purple-500 hover:text-white'
                    }`}
                  >
                    {site.site_name}
                  </button>
                );
              })}
            </div>

            {/* Search + Clear Filters - Right side */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={filters.searchText}
                  onChange={(e) => handleFilterChange('searchText', e.target.value)}
                  className="w-56 bg-slate-800 border border-slate-600 rounded pl-7 pr-2 py-1 text-xs text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                />
                <div className="absolute inset-y-0 left-0 pl-2 flex items-center">
                  <svg className="h-3.5 w-3.5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"></path>
                  </svg>
                </div>
              </div>
              {hasActiveFilters() && (
                <button
                  onClick={clearFilters}
                  className="px-2.5 py-1 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded font-medium transition-colors duration-200 whitespace-nowrap"
                  data-testid="clear-filters-btn"
                  title="Reset every filter on this page to its default (current year, all sites, all statuses, all priorities, no search)"
                >
                  Clear All Filters
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Task Counter */}
        <div className="mb-4">
          <div className="text-sm font-medium text-gray-400">
            Showing {getFilteredTasks().length} of {tasks.length} tasks
          </div>
        </div>

        {/* Task Board with Horizontal Sites and Weekly Layout */}
        {viewMode === 'gantt' ? (
          <div className="min-w-0 w-full overflow-hidden">
            <TaskGanttView
              tasks={getFilteredTasks()}
              users={users}
              selectedYear={selectedYear}
              fromWeek={filters.fromWeek}
              toWeek={filters.toWeek}
              onTaskClick={handleEditTask}
              onTaskReschedule={handleGanttReschedule}
              scrollToCurrentWeekSignal={scrollToWeekSignal}
            />
          </div>
        ) : (
        <div className="overflow-y-auto max-h-[calc(100vh-280px)]">
        {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          <span className="ml-3 text-gray-400">Loading tasks...</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {Object.entries(tasksBySite).map(([siteName, weeklyTasks]) => (
            <div key={siteName} className="bg-slate-800 rounded-xl shadow-lg border border-slate-700">
              {/* Site Header */}
              <div className="px-4 py-3 border-b border-slate-700 bg-slate-900 rounded-t-xl">
                <h3 className="font-medium text-white flex items-center justify-between">
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-6a1 1 0 00-1-1H9a1 1 0 00-1 1v6a1 1 0 01-1 1H4a1 1 0 110-2V4z" clipRule="evenodd"></path>
                    </svg>
                    {siteName}
                  </span>
                  <span className="text-xs bg-purple-600 text-white px-2 py-1 rounded-full">
                    {Object.values(weeklyTasks).flat().length} total
                  </span>
                </h3>
              </div>
              
              {/* Site Users Section (Hidden in Compact View) */}
              {!isCompactView && usersBySite[siteName] && usersBySite[siteName].length > 0 && (
                <div className="px-4 py-3 border-b border-slate-700 bg-slate-800/50">
                  <div className="flex items-center mb-2">
                    <svg className="w-4 h-4 mr-2 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"></path>
                    </svg>
                    <h4 className="font-medium text-gray-300 text-sm">
                      Team ({usersBySite[siteName].length})
                    </h4>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[...usersBySite[siteName]].sort((a, b) => a.full_name.localeCompare(b.full_name)).map((user) => (
                      <div
                        key={user.id}
                        draggable
                        onDragStart={(e) => handleUserDragStart(e, user)}
                        className="inline-flex items-center bg-slate-700 hover:bg-slate-600 text-gray-200 px-2 py-1 rounded-full text-xs font-medium cursor-move transition-colors duration-200 border border-slate-600"
                        title={`Drag to assign ${user.full_name} to a task`}
                      >
                        <span className="max-w-24 truncate">{user.full_name}</span>
                        <svg className="w-3 h-3 ml-1 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path>
                          <path d="M10 4a2 2 0 100-4 2 2 0 000 4z"></path>
                          <path d="M10 20a2 2 0 100-4 2 2 0 000 4z"></path>
                        </svg>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Weekly Timeline - Scrollable */}
              <div className="p-4 max-h-[80vh] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-600 scrollbar-track-slate-800">
                {/* Unscheduled Tasks */}
                {weeklyTasks.unscheduled && weeklyTasks.unscheduled.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center mb-3">
                      <div className="w-3 h-3 bg-gray-500 rounded-full mr-3"></div>
                      <h4 className="font-medium text-gray-300 text-sm">Unscheduled</h4>
                      <span className="ml-2 text-xs bg-slate-700 text-gray-300 px-2 py-1 rounded-full">
                        {weeklyTasks.unscheduled.length}
                      </span>
                    </div>
                    <div className="space-y-2 ml-6">
                      {[...weeklyTasks.unscheduled].sort((a, b) => a.title.localeCompare(b.title)).map((task) => (
                        <div
                          key={task.id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, task)}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleTaskDrop(e, task)}
                          className={`p-3 rounded-lg transition-all duration-200 cursor-move ${
                            task.status === 'completed' 
                              ? 'bg-slate-700/50 opacity-60' 
                              : 'bg-slate-700'
                          } border-l-4 ${getPriorityColor(task.priority)} ${draggedUser ? 'hover:border-purple-400 hover:bg-slate-600' : 'hover:bg-slate-600'}`}
                        >
                          <TaskCard task={task} isCompact={isCompactView} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Weekly Markers */}
                <div className="space-y-4">
                  {getWeeklyMarkers().map((week) => {
                    const weekTasks = weeklyTasks[week.weekNumber] || [];
                    return (
                      <div key={week.weekNumber} data-week={week.weekNumber} className="border-l-2 border-slate-600 pl-4 relative">
                        {/* Week Header */}
                        <div className="flex items-center mb-2">
                          <div className="w-3 h-3 bg-purple-500 rounded-full absolute -left-1.5 top-1"></div>
                          <h4 className="font-medium text-white text-sm">
                            Week {week.weekNumber} <span className="text-gray-400 font-normal">({week.dateRange})</span>
                          </h4>
                          {weekTasks.length > 0 && (
                            <span className="ml-auto text-xs bg-purple-600 text-white px-2 py-1 rounded-full">
                              {weekTasks.length}
                            </span>
                          )}
                        </div>
                        
                        {/* Drop Zone */}
                        <div
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, week.weekNumber, siteName)}
                          className={`min-h-[50px] rounded-lg border-2 border-dashed transition-all duration-200 ${
                            weekTasks.length > 0 
                              ? 'border-slate-600 bg-slate-800/50' 
                              : 'border-slate-600 hover:border-purple-500 hover:bg-slate-700/50'
                          }`}
                        >
                          {weekTasks.length > 0 ? (
                            <div className="space-y-2 p-2">
                              {[...weekTasks].sort((a, b) => {
                                // Sort by start_date when present, fall back to due_date for older tasks.
                                const aAnchor = a.start_date || a.due_date;
                                const bAnchor = b.start_date || b.due_date;
                                if (!aAnchor) return 1;
                                if (!bAnchor) return -1;
                                return new Date(aAnchor) - new Date(bAnchor);
                              }).map((task) => (
                                <div
                                  key={task.id}
                                  draggable
                                  onDragStart={(e) => handleDragStart(e, task)}
                                  onDragOver={handleDragOver}
                                  onDrop={(e) => handleTaskDrop(e, task)}
                                  className={`p-3 rounded-lg transition-all duration-200 cursor-move ${
                                    task.status === 'completed' 
                                      ? 'bg-slate-700/50 opacity-60' 
                                      : 'bg-slate-700'
                                  } border-l-4 ${getPriorityColor(task.priority)} ${draggedUser ? 'hover:border-purple-400 hover:bg-slate-600' : 'hover:bg-slate-600'}`}
                                >
                                  <TaskCard task={task} isCompact={isCompactView} />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="flex items-center justify-center h-12 text-gray-500 text-xs">
                              Drop here
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Show message if site has no tasks at all */}
                {Object.values(weeklyTasks).flat().length === 0 && (
                  <div className="text-center py-8">
                    <svg className="w-8 h-8 mx-auto mb-3 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-6a1 1 0 00-1-1H9a1 1 0 00-1 1v6a1 1 0 01-1 1H4a1 1 0 110-2V4z" clipRule="evenodd"></path>
                    </svg>
                    <p className="text-sm text-gray-400">No tasks for this site</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Create tasks and assign them to {siteName} to see them here
                    </p>
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {Object.keys(tasksBySite).length === 0 && (
            <div className="col-span-full text-center py-12">
              <svg className="w-12 h-12 mx-auto mb-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"></path>
              </svg>
              <h3 className="text-lg font-medium text-white mb-2">No tasks found</h3>
              <p className="text-gray-400">
                {hasActiveFilters() 
                  ? 'No tasks match the current filters. Try adjusting your criteria.' 
                  : 'No tasks available. Create tasks in the Tasks menu to see them here.'
                }
              </p>
            </div>
          )}
        </div>
      )}
      </div>
      )}
      </div>

      {/* Edit Task Modal */}
      {editingTask && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start justify-center pt-8 lg:pt-12 lg:items-center z-50 overflow-y-auto"
          onClick={cancelEditTask}
        >
          <div 
            className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden border border-slate-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header - Purple Gradient */}
            <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-6 py-5">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white">
                    {editingTask.isDuplicate ? 'Duplicate Task' : 'Edit Task'}
                  </h2>
                  <p className="text-purple-200 text-sm">{editingTask.title}</p>
                </div>
                <button
                  onClick={cancelEditTask}
                  className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
              <form onSubmit={handleUpdateTask} className="space-y-4">
                
                {/* Task Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                    Task Title <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Install electrical panel - Floor 2"
                    value={editTask.title}
                    onChange={(e) => setEditTask({...editTask, title: e.target.value.slice(0, 50)})}
                    maxLength={50}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    required
                  />
                  <div className="text-right text-xs text-gray-500 mt-1">{editTask.title.length}/50</div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                    Description
                  </label>
                  <textarea
                    placeholder="Detailed description of the task..."
                    value={editTask.description}
                    onChange={(e) => setEditTask({...editTask, description: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none"
                    rows={3}
                  />
                </div>

                {/* Site Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                    Site <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <select
                      value={editTask.site_id}
                      onChange={(e) => setEditTask({...editTask, site_id: e.target.value})}
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all appearance-none cursor-pointer"
                      required
                    >
                      <option value="" className="bg-slate-800">Select a site</option>
                      {sites.map((site) => (
                        <option key={site.id} value={site.id} className="bg-slate-800">
                          {site.site_name}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Status - Pill Buttons */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                    Status
                  </label>
                  <div className="flex gap-3">
                    {[
                      { value: 'pending', label: 'Pending' },
                      { value: 'in_progress', label: 'In Progress' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'cancelled', label: 'Cancelled' }
                    ].map((status) => (
                      <button
                        key={status.value}
                        type="button"
                        onClick={() => setEditTask({...editTask, status: status.value})}
                        className={`flex-1 py-2.5 px-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                          editTask.status === status.value
                            ? status.value === 'pending' ? 'bg-yellow-500 text-white'
                              : status.value === 'in_progress' ? 'bg-blue-500 text-white'
                              : status.value === 'completed' ? 'bg-green-500 text-white'
                              : 'bg-gray-500 text-white'
                            : 'bg-slate-800 text-gray-300 border border-slate-600 hover:border-slate-500'
                        }`}
                      >
                        {status.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Priority - Pill Buttons */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                    Priority
                  </label>
                  <div className="flex gap-3">
                    {['low', 'medium', 'high', 'urgent'].map((priority) => (
                      <button
                        key={priority}
                        type="button"
                        onClick={() => setEditTask({...editTask, priority})}
                        className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                          editTask.priority === priority
                            ? priority === 'low' ? 'bg-green-500 text-white'
                              : priority === 'medium' ? 'bg-orange-500 text-white'
                              : priority === 'high' ? 'bg-red-500 text-white'
                              : 'bg-purple-600 text-white'
                            : 'bg-slate-800 text-gray-300 border border-slate-600 hover:border-slate-500'
                        }`}
                      >
                        {priority.charAt(0).toUpperCase() + priority.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Start Date & Due Date - Same Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Start Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                      Start Date <span className="text-gray-500">(Optional)</span>
                    </label>
                    <input
                      type="date"
                      value={editTask.start_date}
                      max={editTask.due_date || undefined}
                      onChange={(e) => setEditTask({...editTask, start_date: e.target.value})}
                      className="w-full h-10 px-4 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all [color-scheme:dark]"
                      data-testid="edit-task-start-date-input"
                    />
                  </div>

                  {/* Due Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                      Due Date <span className="text-gray-500">(Optional)</span>
                    </label>
                    <input
                      type="date"
                      value={editTask.due_date}
                      min={editTask.start_date || undefined}
                      onChange={(e) => setEditTask({...editTask, due_date: e.target.value})}
                      className="w-full h-10 px-4 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all [color-scheme:dark]"
                      data-testid="edit-task-due-date-input"
                    />
                  </div>
                </div>
                {editTask.start_date && editTask.due_date && editTask.start_date > editTask.due_date && (
                  <p className="-mt-2 text-xs text-rose-400" data-testid="edit-task-start-date-error">
                    Start date must be on or before the due date.
                  </p>
                )}

                {/* Estimated Workload (own row) */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                    Estimated Workload (Days)
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setEditTask({...editTask, task_capacity: Math.max(0.25, (editTask.task_capacity || 1) - 0.25)})}
                      className="w-8 h-8 rounded-md bg-purple-600 hover:bg-purple-500 text-white font-bold text-sm flex items-center justify-center transition-colors flex-shrink-0"
                      data-testid="edit-task-capacity-decrement-btn"
                    >
                      −
                    </button>
                    <div className="flex-1 h-10 bg-slate-800 border border-slate-600 rounded-lg flex items-center justify-center" data-testid="edit-task-capacity-value">
                      <span className="text-base font-bold text-white">{editTask.task_capacity?.toFixed(2) || '1.00'}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditTask({...editTask, task_capacity: (editTask.task_capacity || 1) + 0.25})}
                      className="w-8 h-8 rounded-md bg-purple-600 hover:bg-purple-500 text-white font-bold text-sm flex items-center justify-center transition-colors flex-shrink-0"
                      data-testid="edit-task-capacity-increment-btn"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Assign Users */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                    Assign Users <span className="text-gray-500">(Optional)</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[...users].sort((a, b) => a.full_name.localeCompare(b.full_name)).map((user) => {
                      const isSelected = editTask.assigned_to.includes(user.id);
                      return (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => {
                            setEditTask({
                              ...editTask,
                              assigned_to: isSelected
                                ? editTask.assigned_to.filter(id => id !== user.id)
                                : [...editTask.assigned_to, user.id]
                            });
                          }}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${
                            isSelected
                              ? 'bg-purple-600 text-white border-purple-500'
                              : 'bg-slate-800 text-gray-300 border-slate-600 hover:border-purple-500'
                          }`}
                        >
                          <span className="font-medium">{user.full_name}</span>
                          {user.role && (
                            <span className={`block text-xs ${isSelected ? 'text-purple-200' : 'text-gray-500'}`}>
                              {user.role}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Linked Materials (Read-Only) */}
                {!editingTask.isDuplicate && editingTask.linked_materials && editingTask.linked_materials.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        Linked Materials ({editingTask.linked_materials.length})
                      </div>
                    </label>
                    <div className="space-y-2">
                      {editingTask.linked_materials.map((material, index) => (
                        <div key={material.id || index} className="bg-slate-800 border border-slate-700 rounded-lg p-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-white">{material.name}</span>
                                <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                                  material.procurement_status === 'placed' 
                                    ? 'bg-green-500/20 text-green-400'
                                    : material.procurement_status === 'archived'
                                    ? 'bg-gray-500/20 text-gray-400'
                                    : 'bg-yellow-500/20 text-yellow-400'
                                }`}>
                                  {material.procurement_status === 'placed' ? 'Order Placed' : 
                                   material.procurement_status === 'archived' ? 'Archived' : 'Pending'}
                                </span>
                              </div>
                              <p className="text-sm text-gray-400">
                                {material.quantity} {material.unit}
                              </p>
                              {material.needed_date && (
                                <p className="text-xs text-purple-400 mt-1">
                                  Needed: {new Date(material.needed_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2 italic">
                      ℹ️ Materials are linked from the Material Requests page
                    </p>
                  </div>
                )}

                {/* Attachments */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-[7px]">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                      </svg>
                      Attachments
                      <span className="text-gray-500 ml-auto">{editSelectedFiles.length + (editingTask?.attachments?.length || 0)}/5</span>
                    </div>
                  </label>
                  <p className="text-xs text-gray-500 mb-3">PDF, Images, Text files • Max 10MB each</p>
                  
                  {/* Existing attachments (for edit mode) */}
                  {editingTask && !editingTask.isDuplicate && editingTask.attachments && editingTask.attachments.length > 0 && (
                    <div className="mb-3 space-y-2">
                      <p className="text-xs text-gray-400">Existing attachments:</p>
                      {editingTask.attachments.map((att, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-gray-300 bg-slate-700/50 px-3 py-2 rounded-lg">
                          <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="truncate flex-1">{att.original_filename || att.filename}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* New files to upload */}
                  {editSelectedFiles.length > 0 && (
                    <div className="mb-3 space-y-2">
                      <p className="text-xs text-gray-400">New attachments:</p>
                      {editSelectedFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-gray-300 bg-slate-700/50 px-3 py-2 rounded-lg">
                          <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="truncate flex-1">{file.name}</span>
                          <span className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</span>
                          <button
                            type="button"
                            onClick={() => removeEditFile(idx)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Add attachment button */}
                  {(editSelectedFiles.length + (editingTask?.attachments?.length || 0)) < 5 && (
                    <label className="flex items-center justify-center gap-2 w-full py-3 border-2 border-dashed border-purple-500/50 rounded-lg text-purple-400 hover:bg-purple-500/10 hover:border-purple-500 transition-all cursor-pointer">
                      <input
                        type="file"
                        ref={editFileInputRef}
                        onChange={handleEditFileSelect}
                        multiple
                        accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif,.webp"
                        className="hidden"
                      />
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Attachment
                    </label>
                  )}
                </div>
              </form>
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 bg-slate-800/50 border-t border-slate-700 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={cancelEditTask}
                disabled={editUploadingFiles}
                className="px-6 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateTask}
                disabled={!editTask.title || !editTask.site_id || editUploadingFiles}
                className="px-6 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {editUploadingFiles ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Uploading...
                  </>
                ) : (
                  editingTask.isDuplicate ? 'Create Task' : 'Update Task'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Task Modal */}
      <AddTaskModal
        isOpen={showAddTaskModal}
        onClose={() => setShowAddTaskModal(false)}
        onTaskCreated={() => {
          fetchTasks();
          setShowAddTaskModal(false);
        }}
      />

      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.show}
        message={confirmModal.message}
        onConfirm={() => {
          if (confirmModal.onConfirm) {
            confirmModal.onConfirm();
          }
          setConfirmModal({ show: false, message: '', onConfirm: null });
        }}
        onClose={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
      />

      <SuccessModal
        isOpen={successModal.show}
        message={successModal.message}
        onClose={() => setSuccessModal({ show: false, message: '' })}
      />

      <ErrorModal
        isOpen={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: '' })}
      />
      </div>
    </>
  );
};

export default TaskLists;